<template>
  <div>

    <template v-if="season !== null">
      <table class="prop-table mb-4">
        <tr>
          <th>Type</th>
          <td>{{ typeName }}</td>
        </tr>
        <tr>
          <th>Status</th>
          <td :class="statusClass">
            {{ season.status | getSeasonStatusName }}
          </td>
        </tr>
        <tr>
          <th>Client min version</th>
          <td>{{ season.client_min_version | convertOptionalVersionCodeToStr('not specified') }}</td>
        </tr>
        <tr>
          <th>Created at</th>
          <td>{{ season.ctime | getMomentStr }}</td>
        </tr>
        <tr>
          <th>Planned start</th>
          <td>{{ season.stime | getMomentStr }}</td>
        </tr>
        <tr>
          <th>Real start</th>
          <td>{{ season.srtime | getSeasonRealStartTimeStr }}</td>
        </tr>
        <tr>
          <th>Remaining time</th>
          <td>{{ remainingTimeStr }}</td>
        </tr>
        <tr>
          <th>Planned finish</th>
          <td>{{ season.ftime | getMomentStr }}</td>
        </tr>
        <tr>
          <th>Real finish</th>
          <td>{{ season.frtime | getSeasonRealFinishTimeStr }}</td>
        </tr>
        <tr>
          <th>Planned duration</th>
          <td>{{ plannedDurationStr }}</td>
        </tr>
        <tr>
          <th>Real duration</th>
          <td>{{ realDurationStr }}</td>
        </tr>
      </table>

      <season-common-score
        v-if="isCommonScoreVisible"
        :seasonId="seasonId"
        :info="season.commonScore"
        :canUserEditCorrection="canUserEditCorrection"
      />

      <div class="mt-5">
        <b-button
          v-if="mustShowForceStartButton"
          @click="forceStartSeasonWithConfirmation()"
        >
          Force start
        </b-button>
      </div>
    </template>

    <p v-if="message !== null">
      {{ message }}
    </p>

    <loading-indicator v-if="isLoading"/>

    <!--
      <b-button @click="fixSeason">
        Fix
      </b-button>
    -->

  </div>
</template>

<script>
import auth from '../auth.js';
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import { SEASON_STATUS_INCOMING            } from '../model/season.js';
import { SEASON_STATUS_ACTIVE              } from '../model/season.js';

import { getSeasonStatusName               } from '../model/season.js';
import { getSeasonStatusClass              } from '../model/season.js';
import { getSeasonRealStartTimeStr         } from '../model/season.js';
import { getSeasonRealFinishTimeStr        } from '../model/season.js';
import { getSeasonRemainingTimeStr         } from '../model/season.js';
import { getSeasonPlannedDurationStr       } from '../model/season.js';
import { getSeasonRealDurationStr          } from '../model/season.js';
import { mayForceStartSeason               } from '../model/season.js';
import { canUserEditCorrection             } from '../model/season.js';

import { confirmAction } from '../service/confirm.js';

import LoadingIndicator from './LoadingIndicator.vue';
import SeasonCommonScore from './SeasonCommonScore.vue';

import NowSecMixin from '../NowSecMixin.js';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'SeasonInfo',

  components: {
    LoadingIndicator,
    SeasonCommonScore,
  },

  mixins: [
    NowSecMixin,
  ],

  props: {
    seasonId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,
      season: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    message() {
      if(this.isLoadedNormal && this.season === null) {
        return `Season with id ${this.seasonId} does not exist.`;
      }
      return null;
    },

    typeName() {
      const t = this.season.type;
      if(t === undefined || t === null) {
        return 'Unknown type';
      }
      return t.name;
    },

    statusClass() {
      return getSeasonStatusClass(this.season.status);
    },

    remainingTimeStr() {
      return getSeasonRemainingTimeStr(this.season, this.nowSec);
    },

    plannedDurationStr() {
      return getSeasonPlannedDurationStr(this.season);
    },

    realDurationStr() {
      return getSeasonRealDurationStr(this.season);
    },

    mustShowForceStartButton() {
      if(!mayForceStartSeason(this.actor.role)) {
        return false;
      }
      if(this.season === null) {
        return false; // data not loaded
      }
      return this.season.status === SEASON_STATUS_INCOMING;
    },

    /** @returns {boolean} */
    isCommonScoreVisible() {
      return this.season.hasOwnProperty('commonScore');
    },

    /** @returns {boolean} */
    canUserEditCorrection() {
      const userRole = this.actor.role;
      const seasonStatus = this.season.status;
      return canUserEditCorrection(userRole, seasonStatus);
    },
  },

  created() {
    this.loadSeason();
  },

  methods: {
    loadSeason() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/season/${this.seasonId}/info`;
      HTTP.get(url)
      .then(response => {
        this.season = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err.response.data, {sender: this});
      });
    },

    forceStartSeasonWithConfirmation() {
      this.confirmForceStart(() => {
        this.forceStart();
      });
    },

    // function onConfirmed()
    confirmForceStart(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to force start this season {env}?',
        'Force start',
        onConfirmed,
      );
    },

    forceStart() {
      notifier.deleteSenderNotifications(this);

      const url = `/season/${this.season.id}/force_start`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.season.srtime = data.srtime;
        this.season.status = SEASON_STATUS_ACTIVE;
        notifier.showSuccess('Season started.');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    /*
    fixSeason() {
      console.log('fixSeason');

      const seasonId = this.seasonId;
      console.log('seasonId:', seasonId);
      if(seasonId !== 87) {
        console.log('Can not fix season. Season id is not 87.');
        return;
      }

      // 2024.04.04
      //const ftime = 1712214000;

      const url = `/season/${this.seasonId}`;
      console.log(url);

      HTTP.post(url)
      .then(() => {
        notifier.showSuccess('Season fixed.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });

    },
    */
  },

  filters: {
    getSeasonStatusName,
    getSeasonRealStartTimeStr,
    getSeasonRealFinishTimeStr,
  },
};
</script>
